import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { registerWithUsername, validateUsername, emailToUsername } from '@/lib/auth'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { toast } from '@/lib/toast'
import { PsgcCascade } from '@/components/PsgcCascade'
import { PasswordInput } from '@/components/PasswordInput'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Check, X } from 'lucide-react'

// ============================================================================
// DEMO MODE — Hardcoded OTP for testing
// TODO: REMOVE BEFORE PRODUCTION — wire up real SMS provider (Twilio/Semaphore/etc.)
// ============================================================================
const DEMO_OTP = '123456'

export function RegisterPage() {
  const navigate = useNavigate()
  const { session, needsOnboarding } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [loading] = useState(false)

  // Form fields
  const [mobileDigits, setMobileDigits] = useState('') // just the 10 digits (09XXXXXXXX)
  const [username, setUsername] = useState('')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [usernameChecking, setUsernameChecking] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Address
  const [regionPsgc, setRegionPsgc] = useState<string | null>(null)
  const [provincePsgc, setProvincePsgc] = useState<string | null>(null)
  const [cityMuniPsgc, setCityMuniPsgc] = useState<string | null>(null)
  const [barangayPsgc, setBarangayPsgc] = useState<string | null>(null)

  // OTP state
  const [showOtp, setShowOtp] = useState(false)
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', ''])
  const [verifying, setVerifying] = useState(false)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])
  const usernameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Mobile number handler — digits only, max 10 ──
  function handleMobileChange(raw: string) {
    // Strip everything except digits
    const digits = raw.replace(/\D/g, '')
    // Cap at 10 digits
    setMobileDigits(digits.slice(0, 10))
  }

  const mobileDisplay = mobileDigits // raw digits shown in the input
  const fullMobileNumber = mobileDigits.length === 10 ? `+63${mobileDigits.slice(1)}` : ''
  const isMobileValid = mobileDigits.length === 10 && mobileDigits.startsWith('09')
  const mobileError =
    mobileDigits.length > 0 && mobileDigits.length < 10
      ? `${10 - mobileDigits.length} more digit${10 - mobileDigits.length > 1 ? 's' : ''} needed`
      : mobileDigits.length === 10 && !mobileDigits.startsWith('9')
        ? 'Must start with 09'
        : null

  // ── Username validation (format + real-time uniqueness check) ──
  useEffect(() => {
    // Reset states
    setUsernameAvailable(null)

    if (!username) {
      setUsernameError(null)
      return
    }

    // First: local format validation
    const validationResult = validateUsername(username)
    if (validationResult.ok === false) {
      setUsernameError(validationResult.reason)
      return
    }

    setUsernameError(null)

    // Then: debounced uniqueness check against the DB
    if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current)

    usernameCheckTimer.current = setTimeout(async () => {
      setUsernameChecking(true)
      try {
        const { data } = await supabase.rpc('check_username_available', {
          _username: username.toLowerCase().trim(),
        })
        const result = data as { available: boolean; reason?: string } | null
        if (result && !result.available) {
          setUsernameError('This username is already taken')
          setUsernameAvailable(false)
        } else if (result?.available) {
          setUsernameAvailable(true)
        }
      } catch {
        // Silently fail — the server-side check at registration will catch it
      } finally {
        setUsernameChecking(false)
      }
    }, 500) // 500ms debounce

    return () => {
      if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current)
    }
  }, [username])

  // Resume onboarding
  useEffect(() => {
    if (session && needsOnboarding && !username) {
      const extracted = emailToUsername(session.user.email ?? '')
      if (extracted) setUsername(extracted)
    }
  }, [session, needsOnboarding, username])

  // Password validation
  const passwordError = validatePassword(password)
  const confirmError =
    confirmPassword.length > 0 && password !== confirmPassword
      ? 'Passwords do not match'
      : null
  const passwordsValid =
    password.length >= 8 && password.length <= 72 && password === confirmPassword

  // Address cascade handlers
  const handleRegionChange = useCallback((v: string) => {
    setRegionPsgc(v); setProvincePsgc(null); setCityMuniPsgc(null); setBarangayPsgc(null)
  }, [])
  const handleProvinceChange = useCallback((v: string) => {
    setProvincePsgc(v); setCityMuniPsgc(null); setBarangayPsgc(null)
  }, [])
  const handleCityMuniChange = useCallback((v: string) => {
    setCityMuniPsgc(v); setBarangayPsgc(null)
  }, [])
  const handleBarangayChange = useCallback((v: string) => {
    setBarangayPsgc(v)
  }, [])

  // ── OTP input handlers ──

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return
    const next = [...otpDigits]
    next[index] = value
    setOtpDigits(next)
    if (value && index < 5) otpRefs.current[index + 1]?.focus()
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  // ── REGISTER: validate form → show OTP ──

  const handleRegister = () => {
    setError(null)
    if (!isMobileValid) { setError('Enter a valid 10-digit mobile number starting with 09'); return }
    if (!username.trim()) { setError('Username is required'); return }
    if (usernameError) return
    if (!password) { setError('Password is required'); return }
    if (passwordError) { setError(passwordError); return }
    if (password !== confirmPassword) { setError('Passwords do not match'); return }
    if (!barangayPsgc) { setError('Please select your barangay'); return }

    setShowOtp(true)
    setOtpDigits(['', '', '', '', '', ''])
    toast.info('Demo mode: use code 123456')
    setTimeout(() => otpRefs.current[0]?.focus(), 100)
  }

  // ── SUBMIT OTP: verify code → create auth → create profile → done ──

  const handleSubmitOtp = async () => {
    const code = otpDigits.join('')
    if (code.length !== 6) { setError('Enter all 6 digits'); return }
    setError(null)
    setVerifying(true)

    // Check OTP
    await new Promise((r) => setTimeout(r, 400))
    if (code !== DEMO_OTP) {
      setError('Invalid code. Please try again.')
      setVerifying(false)
      return
    }

    toast.success('Mobile number verified')

    try {
      // Create auth account with the password the user typed
      if (!session) {
        const { error: authError } = await registerWithUsername(username, password)
        if (authError) {
          if (authError.message.includes('already') || authError.message.includes('registered')) {
            setError('This username is already taken')
          } else if (authError.message.includes('Failed to fetch') || authError.message.includes('NetworkError')) {
            setError('Network error — check your internet connection and try again')
          } else {
            setError(authError.message)
          }
          setVerifying(false)
          return
        }
      }

      // Create resident profile via simplified RPC
      // Send the full +63 format for storage
      const { data, error: rpcError } = await supabase.rpc('register_resident_simple', {
        _username: username.toLowerCase().trim(),
        _mobile_number: fullMobileNumber,
        _barangay_psgc_code: barangayPsgc!,
      })

      if (rpcError) throw rpcError
      if (data && typeof data === 'object' && 'error' in data) {
        const errKey = (data as { error: string }).error
        if (errKey === 'username_taken') {
          setError('This username is already taken')
        } else if (errKey === 'resident_already_exists') {
          setError('You already have an account')
        } else {
          setError(errKey)
        }
        setVerifying(false)
        return
      }

      toast.success('Account created! Complete your profile to get verified.')
      navigate('/')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed'
      if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        setError('Network error — check your internet connection')
      } else {
        setError(message)
      }
    } finally {
      setVerifying(false)
    }
  }

  // ── Render ──

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted-foreground">Join your barangay community</p>

      <div className="rounded-3xl border border-border bg-white p-5 space-y-3.5">
        <h3 className="text-center text-base font-semibold text-foreground">Registration</h3>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-center text-sm text-red-600">{error}</div>
        )}

        {/* Mobile Number — fixed +63 prefix, 10-digit input, number pad */}
        <div>
          <div className="relative flex items-center">
            <span className="absolute left-4 text-sm font-medium text-muted-foreground pointer-events-none select-none">
              
            </span>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="09XXXXXXXXX"
              value={mobileDisplay}
              onChange={(e) => handleMobileChange(e.target.value)}
              disabled={showOtp}
              maxLength={10}
              autoFocus
              className="w-full h-11 rounded-full border border-border bg-white pl-12 pr-4 text-sm text-center placeholder:text-muted-foreground disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
            />
          </div>
          {mobileError && (
            <p className="text-xs text-muted-foreground text-center mt-1">{mobileError}</p>
          )}
          {isMobileValid && (
            <p className="text-xs text-green-600 text-center mt-1 flex items-center justify-center gap-1">
              <Check className="h-3 w-3" /> {fullMobileNumber}
            </p>
          )}
        </div>

        {/* Username — with real-time uniqueness check */}
        <div>
          <div className="relative">
            <Input
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={showOtp}
              className={`h-11 rounded-full border bg-white text-center text-sm placeholder:text-muted-foreground disabled:opacity-60 ${
                usernameError ? 'border-red-500' : usernameAvailable ? 'border-green-500' : 'border-border'
              }`}
            />
            {usernameChecking && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          {usernameError && (
            <p className="text-xs text-red-600 text-center mt-1 flex items-center justify-center gap-1">
              <X className="h-3 w-3" /> {usernameError}
            </p>
          )}
          {!usernameError && usernameAvailable && username.length >= 4 && (
            <p className="text-xs text-green-600 text-center mt-1 flex items-center justify-center gap-1">
              <Check className="h-3 w-3" /> Username available
            </p>
          )}
          {!usernameError && !usernameAvailable && username.length > 0 && username.length < 4 && (
            <p className="text-xs text-muted-foreground text-center mt-1">
              4–24 characters, lowercase letters, digits, dots, underscores
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <PasswordInput
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={showOtp}
            className="h-11 rounded-full border-border bg-white text-center text-sm placeholder:text-muted-foreground disabled:opacity-60"
          />
          {password.length > 0 && (
            <div className="mt-1 space-y-0 px-2">
              <PasswordCheck label="At least 8 characters" met={password.length >= 8} />
              <PasswordCheck label="Has a letter" met={/[a-zA-Z]/.test(password)} />
              <PasswordCheck label="Has a number" met={/\d/.test(password)} />
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <PasswordInput
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={showOtp}
            className="h-11 rounded-full border-border bg-white text-center text-sm placeholder:text-muted-foreground disabled:opacity-60"
          />
          {confirmPassword.length > 0 && (
            confirmError ? (
              <p className="text-xs text-red-600 text-center mt-1 flex items-center justify-center gap-1">
                <X className="h-3 w-3" /> {confirmError}
              </p>
            ) : (
              <p className="text-xs text-green-600 text-center mt-1 flex items-center justify-center gap-1">
                <Check className="h-3 w-3" /> Passwords match
              </p>
            )
          )}
        </div>

        {/* Barangay Selection */}
        <div className={showOtp ? 'opacity-60 pointer-events-none' : ''}>
          <Label className="text-xs text-muted-foreground mb-2 block text-center">Select your Barangay</Label>
          <PsgcCascade
            regionPsgc={regionPsgc}
            provincePsgc={provincePsgc}
            cityMuniPsgc={cityMuniPsgc}
            barangayPsgc={barangayPsgc}
            onRegionChange={handleRegionChange}
            onProvinceChange={handleProvinceChange}
            onCityMuniChange={handleCityMuniChange}
            onBarangayChange={handleBarangayChange}
          />
        </div>

        {/* REGISTER button */}
        {!showOtp && (
          <button
            type="button"
            onClick={handleRegister}
            disabled={loading || !isMobileValid || !!usernameError || !passwordsValid}
            className="mx-auto flex h-11 w-full items-center justify-center rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            REGISTER
          </button>
        )}

        {/* OTP section */}
        {showOtp && (
          <div className="border-t border-border pt-4 space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Enter the 6-digit code sent to <span className="font-medium text-foreground">{fullMobileNumber}</span>
            </p>

            <div className="flex justify-center gap-2">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { otpRefs.current[i] = el }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="h-12 w-10 rounded-lg border border-border bg-white text-center text-lg font-mono font-bold focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              ))}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Demo — use code <span className="font-mono font-semibold">123456</span>
            </p>

            <button
              type="button"
              onClick={handleSubmitOtp}
              disabled={verifying || otpDigits.join('').length !== 6}
              className="mx-auto flex h-11 w-full items-center justify-center rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {verifying ? <Loader2 className="h-5 w-5 animate-spin" /> : 'SUBMIT OTP'}
            </button>

            <button
              type="button"
              onClick={() => { setShowOtp(false); setOtpDigits(['', '', '', '', '', '']); setError(null) }}
              className="block mx-auto text-sm text-muted-foreground hover:text-red-600"
            >
              ← Edit details
            </button>
          </div>
        )}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-red-600 hover:underline">Sign in</Link>
      </p>
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function validatePassword(pw: string): string | null {
  if (pw.length === 0) return 'Password is required'
  if (pw.length < 8) return 'Password must be at least 8 characters'
  if (pw.length > 72) return 'Password must be at most 72 characters'
  return null
}

function PasswordCheck({ label, met }: { label: string; met: boolean }) {
  return (
    <div
      className={`text-xs flex items-center justify-center gap-1.5 ${
        met ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground'
      }`}
    >
      {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </div>
  )
}
